import type { BaseModelAttributes } from "./base";
import { Locale } from "./locale";
import type { I18nLabels } from "./common";

export enum CreditTransactionType {
  RECHARGE = "recharge",
  CONSUME = "consume",
  REFUND = "refund",
  ADJUSTMENT = "adjustment",
  EXPIRE = "expire",
}

export const CreditTransactionTypeLabels: I18nLabels<CreditTransactionType> = {
  [CreditTransactionType.RECHARGE]: { [Locale.ZH_CN]: "充值", [Locale.EN_US]: "Recharge" },
  [CreditTransactionType.CONSUME]: { [Locale.ZH_CN]: "消费", [Locale.EN_US]: "Consume" },
  [CreditTransactionType.REFUND]: { [Locale.ZH_CN]: "退款", [Locale.EN_US]: "Refund" },
  [CreditTransactionType.ADJUSTMENT]: { [Locale.ZH_CN]: "调整", [Locale.EN_US]: "Adjustment" },
  [CreditTransactionType.EXPIRE]: { [Locale.ZH_CN]: "过期", [Locale.EN_US]: "Expire" },
};

export enum CreditSourceType {
  ADMIN_RECHARGE = "admin_recharge",
  ADMIN_ADJUSTMENT = "admin_adjustment",
  USER_RECHARGE = "user_recharge",
  MODEL_CALL = "model_call",
  SYSTEM = "system",
}

export const CreditSourceTypeLabels: I18nLabels<CreditSourceType> = {
  [CreditSourceType.ADMIN_RECHARGE]: { [Locale.ZH_CN]: "管理员充值", [Locale.EN_US]: "Admin Recharge" },
  [CreditSourceType.ADMIN_ADJUSTMENT]: { [Locale.ZH_CN]: "管理员调整", [Locale.EN_US]: "Admin Adjustment" },
  [CreditSourceType.USER_RECHARGE]: { [Locale.ZH_CN]: "用户充值", [Locale.EN_US]: "User Recharge" },
  [CreditSourceType.MODEL_CALL]: { [Locale.ZH_CN]: "模型调用", [Locale.EN_US]: "Model Call" },
  [CreditSourceType.SYSTEM]: { [Locale.ZH_CN]: "系统", [Locale.EN_US]: "System" },
};

/** Credit values are stored and displayed with two decimal places. */
export const CREDIT_DECIMAL_PLACES = 2;

const CREDIT_SCALE = 10 ** CREDIT_DECIMAL_PLACES;

/** Normalize a credit amount to a non-negative value with two decimal places. */
export function normalizeCreditAmount(value: unknown): number {
  const numeric = typeof value === "string"
    ? Number(value.trim())
    : typeof value === "number"
      ? value
      : NaN;
  if (!Number.isFinite(numeric)) return 0;
  return Math.max(0, Math.round((numeric + Number.EPSILON) * CREDIT_SCALE) / CREDIT_SCALE);
}

/** Format a credit amount as a fixed two-decimal string. */
export function formatCreditAmount(value: unknown): string {
  return normalizeCreditAmount(value).toFixed(CREDIT_DECIMAL_PLACES);
}

/** Format a credit amount with the default unit suffix. */
export function formatCreditAmountWithUnit(value: unknown): string {
  return `${formatCreditAmount(value)} 积分`;
}

export interface ICreditAccount extends BaseModelAttributes {
  userId: string;
  balance: number;
  totalRecharged: number;
  totalConsumed: number;
  totalRefunded: number;
  totalExpired: number;
}

export interface ICreditBatch extends BaseModelAttributes {
  userId: string;
  sourceType: CreditSourceType;
  sourceId: string | null;
  amount: number;
  remaining: number;
  expiresAt: Date | string;
  isExpired: boolean;
}

export interface ICreditTransaction extends BaseModelAttributes {
  userId: string;
  type: CreditTransactionType;
  sourceType: CreditSourceType;
  sourceId: string | null;
  batchId: string | null;
  amount: number;
  balanceBefore: number;
  balanceAfter: number;
  description: string;
}

export interface CreditAccountUserSummary {
  id: string;
  username: string;
  email: string;
  name: string;
  deletedAt: Date | string | null;
}

export interface CreditAccountRow extends ICreditAccount {
  user: CreditAccountUserSummary;
}

export interface UserCreditAccountResponse {
  balance: number;
  totalRecharged: number;
  totalConsumed: number;
  totalRefunded: number;
  totalExpired: number;
}

export interface CreditBatchSummary {
  id: string;
  amount: number;
  remaining: number;
  expiresAt: Date | string;
  isExpired: boolean;
}

export interface CreditTransactionRow extends ICreditTransaction {
  user?: CreditAccountUserSummary;
  batch?: CreditBatchSummary;
}

export interface CreditAccountQuery {
  search?: string;
}

export interface CreditTransactionQuery {
  userId?: string;
  type?: CreditTransactionType;
  sourceType?: CreditSourceType;
  startDate?: string;
  endDate?: string;
}

export interface AdminRechargeCreditsRequest {
  userId: string;
  amount: number;
  expiresAt: string;
  description?: string;
}
